import type { SldDocument, StructuralError } from "../services/sld";
import type { SldStatus, SldWarningCode, StructuralErrorCode } from "../services/sldContract";
import { StatusIcon } from "./StatusIcon";
import type { StatusTone } from "./statusTone";

// The validation panel of the single-line diagram editor (SLD-a, Master Prompt A26 (c),
// (d)): the saved check of the stored document as the status banner, its warnings as
// warning-tone cards, and the structural errors of the last refused save as fail-tone
// cards - the shared UX-1 contracts (data-summary-tone, li[data-warning-tone], StatusIcon)
// and no colour of its own. Every item names its element; when the draft has a row for
// that element the name is a button that leads to the row, otherwise it is plain text.

export const STRUCTURE_HEADING =
  "The diagram was not saved. Correct every structural error below:";
export const NOT_SAVED_STATUS = "Not saved yet";
export const NOT_SAVED_NOTE = "This revision has no saved diagram yet.";
export const REFUSED_NOTE =
  "Your latest changes were not saved; see the structural errors below.";

// Exhaustive maps (SldValidationPanel.test.tsx holds them to sldContract): a code the
// backend adds must get words here before the panel can show it.
/** The words after "Saved version N: " - the banner always describes the stored version. */
export const STATUS_LABELS: Record<SldStatus, string> = {
  VALID: "valid",
  WARNING: "saved with warnings",
};

const STATUS_TONES: Record<SldStatus, StatusTone> = {
  VALID: "pass",
  WARNING: "warning",
};

export const WARNING_CODE_LABELS: Record<SldWarningCode, string> = {
  BUS_NOT_ENERGISED: "Bus not energised",
  BUS_EMPTY: "Bus empty",
  SOURCE_NOT_CONNECTED: "Source not connected",
};

export const STRUCTURAL_ERROR_LABELS: Record<StructuralErrorCode, string> = {
  DUPLICATE_CODE: "Duplicate code",
  UNKNOWN_ENDPOINT: "Unknown endpoint",
  WRONG_ENDPOINT_KIND: "Wrong endpoint kind",
  SELF_LOOP: "Self-loop",
  CYCLE: "Loop of buses",
  SECOND_UPSTREAM_FEEDER: "Second upstream feeder",
  LOAD_NOT_CONNECTED: "Load not connected",
  VOLTAGE_MISMATCH_FEEDER: "Voltage mismatch on feeder",
  VOLTAGE_MISMATCH_TRANSFORMER: "Voltage mismatch on transformer",
  VOLTAGE_MISMATCH_INCOMER: "Voltage mismatch on incomer",
};

type SldValidationPanelProps = {
  /** The stored document; nothing is shown until it is loaded. */
  document: SldDocument | null;
  /** The structural errors of the last save attempt; empty once a save went through. */
  errors: readonly StructuralError[];
  /** Whether the draft has a row for an element, device or cable code. */
  hasRow: (code: string) => boolean;
  /** Scrolls to and focuses the row of a code the draft holds. */
  onGoTo: (code: string) => void;
};

function warningCount(count: number): string {
  const words = count === 0 ? "No warnings" : count === 1 ? "1 warning" : `${count} warnings`;
  return `${words} in the saved check.`;
}

function ElementLink({
  code,
  hasRow,
  onGoTo,
}: {
  code: string;
  hasRow: (code: string) => boolean;
  onGoTo: (code: string) => void;
}) {
  return hasRow(code) ? (
    <button type="button" data-sld-go-to={code} onClick={() => onGoTo(code)}>
      Go to {code}
    </button>
  ) : (
    <span data-sld-no-row={code}>{code} is not in the draft.</span>
  );
}

export function SldValidationPanel({ document, errors, hasRow, onGoTo }: SldValidationPanelProps) {
  if (document === null) {
    return null;
  }
  const saved = document.version > 0;
  const status = document.check.status;
  const warnings = document.check.warnings;
  const tone: StatusTone = saved ? STATUS_TONES[status] : "not-evaluated";

  return (
    <section aria-label="Diagram validation" data-summary-tone={tone}>
      <header>
        <p data-summary-status={saved ? status : "NOT_SAVED"}>
          <StatusIcon tone={tone} size={20} />
          {saved ? `Saved version ${document.version}: ${STATUS_LABELS[status]}` : NOT_SAVED_STATUS}
        </p>
        <p>{saved ? warningCount(warnings.length) : NOT_SAVED_NOTE}</p>
        {errors.length > 0 ? <p data-sld-refused="true">{REFUSED_NOTE}</p> : null}
      </header>
      {saved && warnings.length > 0 ? (
        <section aria-label="Warnings">
          <h4>Warnings</h4>
          <ul data-warning-count={warnings.length}>
            {warnings.map((warning, index) => (
              <li
                key={`${warning.code}-${warning.element}-${index}`}
                data-warning-code={warning.code}
                data-warning-tone="warning"
                data-sld-element={warning.element}
              >
                <strong>
                  <StatusIcon tone="warning" size={16} />
                  {WARNING_CODE_LABELS[warning.code]}
                </strong>
                {": "}
                <span>{warning.message}</span>{" "}
                <ElementLink code={warning.element} hasRow={hasRow} onGoTo={onGoTo} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {errors.length > 0 ? (
        <section aria-label="Structural errors" data-save-state="structure">
          <p role="alert">{STRUCTURE_HEADING}</p>
          <ul data-warning-count={errors.length}>
            {errors.map((error, index) => (
              <li
                key={`${error.code}-${error.element}-${index}`}
                data-structural-error={error.code}
                data-warning-tone="fail"
                data-sld-element={error.element}
              >
                <strong>
                  <StatusIcon tone="fail" size={16} />
                  {STRUCTURAL_ERROR_LABELS[error.code]}
                </strong>
                {": "}
                <span>{error.message}</span>{" "}
                <ElementLink code={error.element} hasRow={hasRow} onGoTo={onGoTo} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </section>
  );
}

export default SldValidationPanel;
